import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Pagination from '@amzn/meridian/pagination';
import Link from '@amzn/meridian/link';
import Select, { SelectOption } from '@amzn/meridian/select';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import DataTable, {
    DataTableBody,
    DataTableHeader,
    DataTableHead,
    DataTableCell,
    DataTableRow,
} from '@amzn/meridian/data-table';
import { getBatchEvents, getPresignedDownloadUrls, triggerWeeklyDeployment } from '../services/api';
import { BATCH_EVENT_TABLE } from '../appConfig';

interface WeeklyRun {
    batch_id: string;
    user?: string;
    start_date?: string;
    end_date?: string;
    created_at?: string;
    weekly_deployment_status?: string;
    step?: string;
    cm_id?: string;
    cims_key?: string;
    error_msg?: string;
    warnings?: string;
}

const DEPLOYMENT_TYPES = [{ value: 'WEEKLY', label: 'Weekly' }];
const MAX_DAYS = 15;
const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 25;
const CM_WORKFLOW_URL = 'https://starkapps-amzl.noc.amazon.dev/cws/change-management/workflow?country=BR&id=';

const toIsoDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const daysBetween = (start: string, end: string) =>
    Math.round((new Date(`${end}T00:00:00`).getTime() - new Date(`${start}T00:00:00`).getTime()) / 86400000) + 1;

// Step is only shown while a run is in progress; finished runs read COMPLETED / FAILED
const formatStatus = (run: WeeklyRun) => {
    const status = run.weekly_deployment_status || '';
    return status === 'IN_PROGRESS' && run.step ? `${status} · ${run.step}` : status;
};

const AdhocDeployments: React.FC = () => {
    const [deploymentType, setDeploymentType] = useState('WEEKLY');
    const [dateRange, setDateRange] = useState<string[]>([]);
    const [runs, setRuns] = useState<WeeklyRun[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeploying, setIsDeploying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const tomorrow = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return toIsoDate(d);
    }, []);

    const loadRuns = useCallback(async (showLoader = true) => {
        if (showLoader) setIsLoading(true);
        try {
            const result = await getBatchEvents(BATCH_EVENT_TABLE, 'WEEKLY');
            const items: WeeklyRun[] = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            items.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            setRuns(items);
        } catch (err: any) {
            setError(err.message || 'Failed to load weekly runs');
        } finally {
            if (showLoader) setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const hasActiveRun = runs.some(r => r.weekly_deployment_status === 'IN_PROGRESS');

    // Show the current run in the table while the deploy request is still open
    useEffect(() => {
        if (!hasActiveRun && !isDeploying) return undefined;
        const timer = setInterval(() => loadRuns(false), POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [hasActiveRun, isDeploying, loadRuns]);

    const [startDate, endDate] = dateRange;
    const rangeError = useMemo(() => {
        if (!startDate || !endDate) return null;
        if (startDate < tomorrow) return `Start date must be ${tomorrow} or later`;
        if (daysBetween(startDate, endDate) > MAX_DAYS) return `Select at most ${MAX_DAYS} days`;
        return null;
    }, [startDate, endDate, tomorrow]);

    const canDeploy = deploymentType === 'WEEKLY' && !!startDate && !!endDate && !rangeError && !isDeploying;

    const handleDeploy = async () => {
        setError(null);
        setMessage(null);
        setIsDeploying(true);
        try {
            const result = await triggerWeeklyDeployment(startDate, endDate);
            if (result?.error) throw new Error(result.error);
            setMessage(`Weekly deployment completed: ${result.batch_id}${result.cm_id ? ` (CM ${result.cm_id})` : ''}`);
        } catch (err: any) {
            // The gateway can time out while the Lambda keeps running; the table has the real outcome
            if (String(err?.message).includes('504')) {
                setMessage('Deployment is still running. Its status will update in the table below.');
            } else {
                setError(`${err.message || 'Weekly deployment failed'}. See the table below for details.`);
            }
        } finally {
            setIsDeploying(false);
            await loadRuns(false);
        }
    };

    const handleDownload = async (key: string) => {
        try {
            const { urls } = await getPresignedDownloadUrls([key]);
            if (urls[key]) window.open(urls[key], '_blank');
        } catch (err: any) {
            setError(err.message || 'Download failed');
        }
    };

    const numberOfPages = Math.max(1, Math.ceil(runs.length / PAGE_SIZE));
    // Clamp so a shrinking result set (e.g. after a refresh) can't strand the user on an empty page.
    const safePage = Math.min(currentPage, numberOfPages);
    const paginatedRuns = runs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const columns = [
        'Batch ID', 'User', 'Dates', 'Status', 'CM ID', 'Created At', 'Details', 'CIMS',
    ];

    return (
        <Column spacing="400">
            <Heading level={4} type="h300">Adhoc Deployments</Heading>

            <Alert type="neutral" size="medium">
                Weekly deploys W-1 forecast caps for the selected OFD dates, using W-1 rules from Cap Release
                Configurations and windows from CIT Mapping. Deployment starts as soon as you click Deploy.
            </Alert>

            <Row spacing="400" alignmentVertical="bottom" wrap="down">
                <Select value={deploymentType} onChange={setDeploymentType} label="Deployment type" width={200}>
                    {DEPLOYMENT_TYPES.map(t => <SelectOption key={t.value} value={t.value} label={t.label} />)}
                </Select>
                {deploymentType === 'WEEKLY' && (
                    <DateRangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        startLabel="Start OFD date"
                        endLabel="End OFD date"
                        allowSameStartAndEnd={true}
                        disabledDates={(date: string) => date < tomorrow}
                        startError={!!rangeError}
                        endError={!!rangeError}
                        errorMessage={rangeError || undefined}
                    />
                )}
                <Button onClick={handleDeploy} disabled={!canDeploy}>
                    {isDeploying ? 'Deploying...' : 'Deploy'}
                </Button>
            </Row>

            {hasActiveRun && (
                <Alert type="neutral" size="small">
                    A weekly deployment is in progress. Check it has finished before deploying the same dates again.
                </Alert>
            )}
            {message && <Alert type="success" size="medium" onClose={() => setMessage(null)}>{message}</Alert>}
            {error && <Alert type="error" size="medium" onClose={() => setError(null)}>{error}</Alert>}

            <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Heading level={5} type="h200">Weekly executions</Heading>
                <Button onClick={() => loadRuns()} disabled={isLoading} type="tertiary">
                    <Icon tokens={refreshTokens}>Refresh</Icon>
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
            </Row>

            {isLoading ? (
                <Row alignmentHorizontal="center" spacingInset="450"><Loader size="small" /></Row>
            ) : (
                <>
                <DataTable size="small" showRowDividers={false} showColumnDividers={false} showStripes={true} showBorder={false}>
                    <DataTableHead>
                        <DataTableRow highlightOnHover={false}>
                            {columns.map(col => <DataTableHeader key={col}>{col}</DataTableHeader>)}
                        </DataTableRow>
                    </DataTableHead>
                    <DataTableBody>
                        {paginatedRuns.map(run => (
                            <DataTableRow key={run.batch_id} rowId={run.batch_id} highlightOnHover={true}>
                                <DataTableCell>{run.batch_id}</DataTableCell>
                                <DataTableCell>{run.user || ''}</DataTableCell>
                                <DataTableCell>{run.start_date && run.end_date ? `${run.start_date} to ${run.end_date}` : ''}</DataTableCell>
                                <DataTableCell>{formatStatus(run)}</DataTableCell>
                                <DataTableCell containsSingleFocusableChild={!!run.cm_id}>
                                    {run.cm_id ? (
                                        <Link href={`${CM_WORKFLOW_URL}${encodeURIComponent(run.cm_id)}`} target="_blank" rel="noopener noreferrer">
                                            {run.cm_id}
                                        </Link>
                                    ) : ''}
                                </DataTableCell>
                                <DataTableCell>{run.created_at || ''}</DataTableCell>
                                <DataTableCell>
                                    {run.weekly_deployment_status === 'FAILED' ? run.error_msg || '' : run.warnings || ''}
                                </DataTableCell>
                                <DataTableCell containsSingleFocusableChild={true}>
                                    {run.cims_key ? (
                                        <Button type="link" size="small" onClick={() => handleDownload(run.cims_key as string)}>
                                            Download
                                        </Button>
                                    ) : ''}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                        {paginatedRuns.length === 0 && (
                            <DataTableRow highlightOnHover={false}>
                                <DataTableCell colSpan={columns.length}>
                                    <Row alignmentHorizontal="center" spacingInset="400">
                                        <Text color="secondary">No weekly executions yet</Text>
                                    </Row>
                                </DataTableCell>
                            </DataTableRow>
                        )}
                    </DataTableBody>
                </DataTable>
                {numberOfPages > 1 && (
                    <Row alignmentHorizontal="end">
                        <Pagination currentPage={safePage} numberOfPages={numberOfPages} onChange={setCurrentPage} showSkipArrows={true} />
                    </Row>
                )}
                </>
            )}
        </Column>
    );
};

export default AdhocDeployments;
